import { api } from './client';
import type { Meal, MealSlot, GroceryItem, InputUnit, MealIngredient, PantryRow } from '../types';

/** The plan and the list travel together: the tab renders both at once. */
export interface MealPlan {
  meals: Meal[];
  grocery: GroceryItem[];
}

export interface MealInput {
  dayId: string;
  slot: MealSlot;
  title: string;
  notes?: string | null;
  venueId?: string | null;
}

export interface NewMealIngredient { name: string; amount: number; unit: InputUnit }

/** ADR-0016: every line is a product with an amount; blank = 1 unit server-side. */
export interface GroceryInput {
  text: string;
  amount?: number;
  unit?: InputUnit;
  bought?: boolean;
  mealId?: string | null;
}

export const mealsApi = {
  list: (tripId: string) => api.get<MealPlan>(`/trips/${tripId}/meals`).then(r => r.data),

  /** Created whole: the ingredients travel with the meal (ADR-0014). */
  create: (tripId: string, data: MealInput & { ingredients?: NewMealIngredient[] }) =>
    api.post<Meal>(`/trips/${tripId}/meals`, data).then(r => r.data),
  update: (tripId: string, mealId: string, data: Partial<Omit<MealInput, 'dayId'>>) =>
    api.patch<Meal>(`/trips/${tripId}/meals/${mealId}`, data).then(r => r.data),
  /** ADR-0024: `withActivity` only when the user said the linked stop goes too. */
  delete: (tripId: string, mealId: string, withActivity = false) =>
    api.delete(`/trips/${tripId}/meals/${mealId}`, withActivity ? { params: { withActivity: 'true' } } : undefined),
  /** Makes the meal a stop of its own day. Without an hour it goes at the end of the day. */
  createActivity: (tripId: string, mealId: string, startTime: string | null) =>
    api.post<Meal>(`/trips/${tripId}/meals/${mealId}/activity`, { startTime }).then(r => r.data),
  /** Links the meal to a stop of its day (it takes the stop's title and place), or unlinks it. */
  linkActivity: (tripId: string, mealId: string, activityId: string | null) =>
    api.patch<Meal>(`/trips/${tripId}/meals/${mealId}/activity`, { activityId }).then(r => r.data),
  reorder: (tripId: string, orderedIds: string[]) =>
    api.patch(`/trips/${tripId}/meals/reorder`, { orderedIds }),

  createGroceryItem: (tripId: string, data: GroceryInput) =>
    api.post<GroceryItem>(`/trips/${tripId}/meals/grocery`, data).then(r => r.data),
  updateGroceryItem: (
    tripId: string, itemId: string,
    data: { bought?: boolean; mealId?: string | null; amount?: number; unit?: InputUnit },
  ) =>
    api.patch<GroceryItem>(`/trips/${tripId}/meals/grocery/${itemId}`, data).then(r => r.data),
  deleteGroceryItem: (tripId: string, itemId: string) =>
    api.delete(`/trips/${tripId}/meals/grocery/${itemId}`),
  reorderGrocery: (tripId: string, orderedIds: string[]) =>
    api.patch(`/trips/${tripId}/meals/grocery/reorder`, { orderedIds }),
  clearBought: (tripId: string) =>
    api.delete<{ deleted: number }>(`/trips/${tripId}/meals/grocery/bought`).then(r => r.data),

  // ADR-0014: `today` is the local date — the server cannot tell the trip's timezone.
  pantry: (tripId: string, today: string) =>
    api.get<PantryRow[]>(`/trips/${tripId}/meals/pantry`, { params: { today } }).then(r => r.data),
  addIngredient: (tripId: string, mealId: string, data: { name: string; amount: number; unit: InputUnit }) =>
    api.post<MealIngredient>(`/trips/${tripId}/meals/${mealId}/ingredients`, data).then(r => r.data),
  updateIngredient: (tripId: string, mealId: string, ingredientId: string, data: { amount: number; unit: InputUnit }) =>
    api.patch<MealIngredient>(`/trips/${tripId}/meals/${mealId}/ingredients/${ingredientId}`, data).then(r => r.data),
  removeIngredient: (tripId: string, mealId: string, ingredientId: string) =>
    api.delete(`/trips/${tripId}/meals/${mealId}/ingredients/${ingredientId}`),
  addMissingToList: (tripId: string, pantryItemId: string, today: string) =>
    api.post<GroceryItem>(`/trips/${tripId}/meals/pantry/${pantryItemId}/to-list`, { today }).then(r => r.data),
};
